import { takeLatest, takeEvery, fork, put, select, race, take } from "redux-saga/effects";
import {
  followedStocksRoutine,
  notifyAboutFetchingFollowedStocks,
  setFollowedStocksMap,
  unfollowStockRoutine,
  getAlertedTagDetailsRoutine,
  followStockRoutine,
  bulkFollowRoutine,
  bulkUnFollowRoutine,
  toggleAlert
} from "./index";
import ApiClient from "../../../api/ApiClient";
import { getAuthToken, getUserData } from "../auth/login/selector";
import { actions as alertActions } from "../alerts";
import decisionModal from "../../../lib/decisionModal";
import AnalyticsEventTracker from "../../../lib/AnalyticsEventTracker";
import {
  disconnectTagFromStockRoutine,
  fetchStocksListFromSourceRoutine,
  fetchStockTagsRoutine,
  updateStocksListSource
} from "../tags";
import { getStocksListSource } from "../tags/selector";
import TagsHelper from "../../../lib/TagsHelper/TagsHelper";
import { getAllTags } from "../tagsService/selector";
import wait from "../../../lib/wait";
import { updateTag } from "../tagsService";
import { trackSMEvent } from "../salesManagoEventTracker";
import StockAlertOnEvent from "../../../lib/SalesManago/events/StockAlertOnEvent";
import StockAlertOffEvent from "../../../lib/SalesManago/events/StockAlertOffEvent";
import { getStockIsFollowed } from "./selector";
import { getUserMarketsState } from "../userMarkets/selector";
import { getUserMarkets } from "../userMarkets";

function* triggerFollowingStocks() {
  let authToken = yield select(getAuthToken);

  if (authToken) {
    yield put(followedStocksRoutine.request());
    yield put(getAlertedTagDetailsRoutine());
  }
}

export function* getFollowedStocks() {
  yield put(notifyAboutFetchingFollowedStocks());

  try {
    const { data } = yield ApiClient.get({
      urlPath: "app.stocks.follow",
      skipAccessCheck: true
    });

    yield updateAlertedTag(data);

    const followedStocksMap = {};

    for (let stock of data.stocks) {
      followedStocksMap[stock.stock.id] = stock.user_stock;
    }

    yield put(followedStocksRoutine.success(data));
    yield put(setFollowedStocksMap(followedStocksMap));
  } catch (error) {
    yield put(followedStocksRoutine.failure(error));
  }
}

function* unfollowStock(stockId, isCurrentStock, ticker, market) {
  const decision = yield decisionModal(
    "ConfirmModal",
    {
      title: "stock-unfollow_title",
      message: "stock-unfollow_description"
    },
    ["confirm", "cancel"],
    {
      confirm: "stock-unfollow_button"
    }
  );

  if (decision === "cancel") {
    return;
  }

  try {
    yield ApiClient.delete({
      urlPath: "app.stocks.unfollow",
      variables: {
        stockId
      }
    });

    yield put(
      alertActions.addSuccessAlert({
        title: "Stock settings changed successfully"
      })
    );

    const smEvent = new StockAlertOffEvent({
      stockId,
      ticker,
      market
    });

    yield put(trackSMEvent(smEvent));

    yield put(followedStocksRoutine());
    yield put(fetchStocksListFromSourceRoutine());

    if (isCurrentStock) {
      yield put(fetchStockTagsRoutine({ stockId }));
    }
  } catch (e) {
    console.log(e);
  }
}

function* removeStockFromTag(stockId, tag, isCurrentStock) {
  const decision = yield decisionModal(
    "ConfirmModal",
    {
      title: "Are you sure?",
      message: "Do you really want to remove this stock from tag?"
    },
    ["confirm", "cancel"]
  );

  if (decision === "cancel") {
    return;
  }

  yield put(
    disconnectTagFromStockRoutine({
      stockId,
      tagId: tag.id,
      shouldRefreshStockPageTags: isCurrentStock
    })
  );
}

function* onFollow({ payload }) {
  const { type, market, ticker, stockId } = payload;

  try {
    yield ApiClient.put({
      urlPath: "app.stocks.toggleAlert",
      variables: {
        stockId: stockId
      }
    });

    yield put(
      alertActions.addSuccessAlert({
        title: "Stock settings changed successfully"
      })
    );

    yield put(followedStocksRoutine());
    yield put(fetchStocksListFromSourceRoutine());

    AnalyticsEventTracker.trackStockAlertOnEvent({
      type,
      market,
      name: ticker
    });

    const smEvent = new StockAlertOnEvent({
      stockId,
      ticker,
      market
    });

    yield put(trackSMEvent(smEvent));
  } catch (e) {
    console.log(e);
  }
}

function* onUnfollow({ payload }) {
  const userData = yield select(getUserData);
  let stockId, ticker, market;

  if (payload !== null && typeof payload === "object") {
    stockId = payload.stockId;
    ticker = payload.ticker;
    market = payload.market;
  } else {
    stockId = payload;
  }

  const { isUnfollowing } = payload;

  const stocksListSource = yield select(getStocksListSource);
  const stocksListSourceIsAlertedTag =
    stocksListSource && TagsHelper.isAlertedTag(stocksListSource);

  if (isUnfollowing || !stocksListSource || stocksListSourceIsAlertedTag) {
    yield unfollowStock(stockId, true, ticker, market, userData);
  } else {
    yield removeStockFromTag(payload, stocksListSource, true);
  }
}

function* fetchAlertedTagDetails() {
  const allTags = yield select(getAllTags);

  if (!allTags.length) {
    yield wait(500);
    return yield fetchAlertedTagDetails();
  }

  return allTags.find(tag => TagsHelper.isAlertedTag(tag)) || null;
}

function* onGetAlertedTagDetails() {
  const alertedTag = yield fetchAlertedTagDetails();

  yield put(getAlertedTagDetailsRoutine.success(alertedTag));
}

function* updateAlertedTag(followedData) {
  let alertedTag = yield fetchAlertedTagDetails();
  const stocksListSource = yield select(getStocksListSource);

  const followedStockIds = followedData.stocks.map(item => item.stock.id);
  const currentStocksListSourceIsAlertedTag =
    stocksListSource && TagsHelper.isAlertedTag(stocksListSource);

  const data = {
    ...alertedTag,
    stocks: followedStockIds
  };

  if (currentStocksListSourceIsAlertedTag) {
    yield put(updateStocksListSource(data));
  }

  yield put(updateTag(data));
  yield put(getAlertedTagDetailsRoutine.success(data));
}

function* onBulkFollow({ payload }) {
  const { stocks } = payload;

  try {
    yield ApiClient.put({
      urlPath: "app.stocks.bulkFollowUnfollow",
      data: {
        stocks
      }
    });

    yield put(
      alertActions.addSuccessAlert({
        title: "Stocks alerted successfully"
      })
    );

    yield put(followedStocksRoutine());
  } catch (e) {
    console.log(e);
  }
}

function* onBulkUnFollow({ payload }) {
  const { stocks } = payload;

  const decision = yield decisionModal(
    "ConfirmModal",
    {
      title: "Are you sure?",
      message:
        "Do you really want to remove alerts from all stocks in this desktop? This will also affect stocks that are not visible on screen right now."
    },
    ["confirm", "cancel"]
  );

  if (decision === "cancel") {
    return;
  }

  try {
    yield ApiClient.delete({
      urlPath: "app.stocks.bulkFollowUnfollow",
      data: {
        stocks
      }
    });

    yield put(
      alertActions.addSuccessAlert({
        title: "Alerts removed successfully"
      })
    );
    yield put(followedStocksRoutine());
  } catch (e) {
    console.log(e);
  }
}

function* onToggleAlert({ payload: { stockId, market, ticker } }) {
  const removingAlert = yield select(getStockIsFollowed, stockId);
  let decision = "confirm";
  let SMEventClass = StockAlertOnEvent;

  if (removingAlert) {
    decision = yield decisionModal(
      "ConfirmModal",
      {
        title: "stock-unfollow_title",
        message: "stock-unfollow_description"
      },
      ["confirm", "cancel"],
      {
        confirm: "stock-unfollow_button"
      }
    );
    SMEventClass = StockAlertOffEvent;
  }

  if (decision === "cancel") {
    return;
  }

  yield put(getUserMarkets());
  const result = yield race({
    success: take(getUserMarkets.SUCCESS),
    failure: take(getUserMarkets.FAILURE)
  });

  let isFollowedMarket = true;

  if (result.success) {
    const userMarketsState = yield select(getUserMarketsState);
    const markets = userMarketsState?.markets ?? [];
    const marketNormalized =
      typeof market === "string" ? market.toLowerCase() : market;

    isFollowedMarket = markets.some(
      followedMarket =>
        typeof followedMarket === "string" &&
        followedMarket.toLowerCase() === marketNormalized
    );

    if (!isFollowedMarket) {
      const marketDecision = yield decisionModal(
        "ConfirmModal",
        {
          title:
            "The stock belongs to a market that you are not currently watching.",
          message:
            "Enabling alerts for this stock will also add the entire market to your watchlist."
        },
        ["confirm", "cancel"]
      );

      if (marketDecision === "cancel") {
        return;
      }
    }
  } else {
    console.warn("Failed to load user markets");
  }

  const smEvent = new SMEventClass({
    stockId,
    ticker,
    market
  });

  yield put(trackSMEvent(smEvent));

  try {
    yield ApiClient.put({
      urlPath: "app.stocks.toggleAlert",
      variables: {
        stockId
      }
    });

    if (!isFollowedMarket) {
      yield put(getUserMarkets());
    }

    yield put(
      alertActions.addSuccessAlert({
        title: "Stock settings changed successfully"
      })
    );
    yield put(toggleAlert.success());
    yield put(followedStocksRoutine());
    yield put(fetchStockTagsRoutine({ stockId }));

    if (!removingAlert) {
      AnalyticsEventTracker.trackStockAlertOnEvent({
        type: "single_stock",
        market,
        name: ticker
      });
    }
  } catch (error) {
    console.log(error);
  }
}

function* watchGetFollowedStocks() {
  yield takeLatest(followedStocksRoutine.TRIGGER, triggerFollowingStocks);
  yield takeLatest(followedStocksRoutine.REQUEST, getFollowedStocks);
  yield takeEvery(followStockRoutine.TRIGGER, onFollow);
  yield takeEvery(unfollowStockRoutine.TRIGGER, onUnfollow);
  yield takeLatest(getAlertedTagDetailsRoutine.TRIGGER, onGetAlertedTagDetails);
  yield takeLatest(bulkFollowRoutine.TRIGGER, onBulkFollow);
  yield takeLatest(bulkUnFollowRoutine.TRIGGER, onBulkUnFollow);
  yield takeLatest(toggleAlert.TRIGGER, onToggleAlert);
}

export default [fork(watchGetFollowedStocks)];
